import type { ConvertedIo, ConvertedTransitionType } from "../types";

/**
 * `項目定義.オブジェクト種別` 列の値を `ConvertedTarget` の `type` + 分類に変換する。
 *
 * デフォルト辞書は新仕様の「カテゴリ / オブジェクト名」形式を正とし、
 * 旧仕様書の短い値は alias として受け付ける。
 */
export type LegendMappingResult = {
  type: "item" | "section" | "overlay" | "component-ref";
  itemCategory?: string;
  itemSubCategory?: string;
};

export type LegendMappingEntry = {
  objectType: string;
  mapping?: LegendMappingResult;
};

const DEFAULT_LEGEND = new Map<string, LegendMappingResult>(normalizeEntries([
  ["Action / a", { type: "item", itemCategory: "Action", itemSubCategory: "link" }],
  ["Action / button", { type: "item", itemCategory: "Action", itemSubCategory: "button" }],
  ["Input / text", { type: "item", itemCategory: "Input", itemSubCategory: "text" }],
  ["Input / tel", { type: "item", itemCategory: "Input", itemSubCategory: "tel" }],
  ["Input / postcode", { type: "item", itemCategory: "Input", itemSubCategory: "postcode" }],
  ["Input / slidebar", { type: "item", itemCategory: "Input", itemSubCategory: "slidebar" }],
  ["Input / date", { type: "item", itemCategory: "Input", itemSubCategory: "date" }],
  ["Input / email", { type: "item", itemCategory: "Input", itemSubCategory: "email" }],
  ["Input / password", { type: "item", itemCategory: "Input", itemSubCategory: "password" }],
  ["Input / number", { type: "item", itemCategory: "Input", itemSubCategory: "number" }],
  ["Input / file", { type: "item", itemCategory: "Input", itemSubCategory: "file" }],
  ["Selection / select", { type: "item", itemCategory: "Selection", itemSubCategory: "select" }],
  ["Selection / radio", { type: "item", itemCategory: "Selection", itemSubCategory: "radio" }],
  ["Selection / checkbox", { type: "item", itemCategory: "Selection", itemSubCategory: "checkbox" }],
  ["Display / text", { type: "item", itemCategory: "Display", itemSubCategory: "text" }],
  ["Display / textarea", { type: "item", itemCategory: "Display", itemSubCategory: "textarea" }],
  ["Display / label", { type: "item", itemCategory: "Display", itemSubCategory: "label" }],
  ["Display / img", { type: "item", itemCategory: "Display", itemSubCategory: "img" }],
  ["Display / video", { type: "item", itemCategory: "Display", itemSubCategory: "video" }],
  ["Display / QRcode", { type: "item", itemCategory: "Display", itemSubCategory: "QRcode" }],
  ["Display / barcode", { type: "item", itemCategory: "Display", itemSubCategory: "barcode" }],
  ["TargetType / overlay", { type: "overlay" }],
  ["TargetType / section", { type: "section" }],
  ["TargetType / component-ref", { type: "component-ref" }],
]));

const LEGEND_ALIASES = new Map<string, LegendMappingResult>(normalizeEntries([
  ["a", { type: "item", itemCategory: "Action", itemSubCategory: "link" }],
  ["link", { type: "item", itemCategory: "Action", itemSubCategory: "link" }],
  ["button", { type: "item", itemCategory: "Action", itemSubCategory: "button" }],
  ["input", { type: "item", itemCategory: "Input", itemSubCategory: "text" }],
  ["select", { type: "item", itemCategory: "Selection", itemSubCategory: "select" }],
  ["radio", { type: "item", itemCategory: "Selection", itemSubCategory: "radio" }],
  ["checkbox", { type: "item", itemCategory: "Selection", itemSubCategory: "checkbox" }],
  ["text", { type: "item", itemCategory: "Display", itemSubCategory: "text" }],
  ["textarea", { type: "item", itemCategory: "Input", itemSubCategory: "textarea" }],
  ["label", { type: "item", itemCategory: "Display", itemSubCategory: "text" }],
  ["img", { type: "item", itemCategory: "Display", itemSubCategory: "file" }],
  ["overlay", { type: "overlay" }],
  ["section", { type: "section" }],
  ["component-ref", { type: "component-ref" }],
  ["mail", { type: "item", itemCategory: "Input", itemSubCategory: "email" }],
  ["email", { type: "item", itemCategory: "Input", itemSubCategory: "email" }],
  ["tell", { type: "item", itemCategory: "Input", itemSubCategory: "tel" }],
  ["tel", { type: "item", itemCategory: "Input", itemSubCategory: "tel" }],
  ["postcode", { type: "item", itemCategory: "Input", itemSubCategory: "postcode" }],
  ["slidebar", { type: "item", itemCategory: "Input", itemSubCategory: "slidebar" }],
  ["slider", { type: "item", itemCategory: "Input", itemSubCategory: "slidebar" }],
  ["range", { type: "item", itemCategory: "Input", itemSubCategory: "slidebar" }],
  ["date", { type: "item", itemCategory: "Input", itemSubCategory: "date" }],
  ["number", { type: "item", itemCategory: "Input", itemSubCategory: "number" }],
  ["password", { type: "item", itemCategory: "Input", itemSubCategory: "password" }],
  ["file", { type: "item", itemCategory: "Input", itemSubCategory: "file" }],
  ["video", { type: "item", itemCategory: "Display", itemSubCategory: "video" }],
  ["qrcode", { type: "item", itemCategory: "Display", itemSubCategory: "QRcode" }],
  ["barcode", { type: "item", itemCategory: "Display", itemSubCategory: "barcode" }],
]));

const DEFAULT_OBJECT_TYPE_KEYS = [
  "Action / a",
  "Action / button",
  "Input / text",
  "Input / tel",
  "Input / postcode",
  "Input / slidebar",
  "Input / date",
  "Input / email",
  "Input / password",
  "Input / number",
  "Input / file",
  "Selection / select",
  "Selection / radio",
  "Selection / checkbox",
  "Display / text",
  "Display / textarea",
  "Display / label",
  "Display / img",
  "Display / video",
  "Display / QRcode",
  "Display / barcode",
  "TargetType / overlay",
  "TargetType / section",
  "TargetType / component-ref",
] as const;

const TRANSITION_TYPE_MAPPING_INTERNAL = new Map<string, ConvertedTransitionType>([
  ["通常遷移", "same_tab"],
  ["別タブ", "new_tab"],
  ["別ウィンドウ", "new_window"],
]);

const IO_MAPPING_INTERNAL = new Map<string, ConvertedIo>([
  ["I (Input)", "input"],
  ["O (Output)", "output"],
  ["I/O", "both"],
  ["S (System)", "system"],
]);

export class LegendMapper {
  /** @param objectType 仕様書の `オブジェクト種別` 列の値 */
  map(objectType: string): LegendMappingResult | undefined {
    const key = normalizeObjectTypeKey(objectType);
    return DEFAULT_LEGEND.get(key) ?? LEGEND_ALIASES.get(key);
  }

  /** カンマ区切りで複数指定された `オブジェクト種別` を分割して lookup する。 */
  mapAll(objectType: string): LegendMappingEntry[] {
    return splitObjectTypes(objectType).map((part) => ({
      objectType: part,
      mapping: this.map(part),
    }));
  }

  lookupTransitionType(key: string): ConvertedTransitionType | undefined {
    return TRANSITION_TYPE_MAPPING_INTERNAL.get(key);
  }

  lookupIo(key: string): ConvertedIo | undefined {
    return IO_MAPPING_INTERNAL.get(key);
  }

  /** デフォルト辞書全体のキー一覧 (凡例一致テスト用) */
  getDefaultObjectTypes(): string[] {
    return [...DEFAULT_OBJECT_TYPE_KEYS];
  }
}

function normalizeObjectTypeKey(objectType: string): string {
  return objectType
    .trim()
    .replace(/\s*[／/]\s*/g, "/")
    .replace(/\s+/g, " ")
    .toLowerCase();
}

function splitObjectTypes(objectType: string): string[] {
  return objectType
    .split(/[,，、]/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

function normalizeEntries(
  entries: [string, LegendMappingResult][],
): [string, LegendMappingResult][] {
  return entries.map(([key, value]) => [normalizeObjectTypeKey(key), value]);
}

/** 凡例一致テスト用。実装側で使う場合は `LegendMapper.lookupTransitionType` を使う。 */
export const TRANSITION_TYPE_KEYS: readonly string[] = Array.from(TRANSITION_TYPE_MAPPING_INTERNAL.keys());

/** 凡例一致テスト用。実装側で使う場合は `LegendMapper.lookupIo` を使う。 */
export const IO_KEYS: readonly string[] = Array.from(IO_MAPPING_INTERNAL.keys());
